import { Signal } from '@latitude/signal';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import type { ComponentRef, Ref } from 'react';
import type {
  AvatarBorder,
  AvatarProps,
  AvatarSize,
  AvatarType,
  AvatarVariant,
} from 'types';
import { avatarVariants, textVariants } from 'variants';

import { getInitials } from './helpers';

const Avatar = ({
  size = 'small',
  variant = 'strong',
  type = 'letter',
  text = '',
  imageSrc,
  icon,
  border = 'none',
  signal,
  additionIcon,
  className,
  ref,
  ...rest
}: AvatarProps & {
  ref?: Ref<ComponentRef<'div'>>;
}) => {
  return (
    <div
      {...rest}
      ref={ref}
      className={cn(avatarVariants({ size, variant, type, border }), className)}
      style={{
        backgroundImage:
          type === 'image' && imageSrc ? `url(${imageSrc})` : undefined,
      }}
      data-testid="avatar-container"
    >
      {type === 'letter' && (
        <Text className={cn(textVariants({ variant, size }))}>
          {getInitials(text)}
        </Text>
      )}

      {type === 'icon' && icon}

      {signal && (
        <Signal
          className={cn([
            'absolute',
            'bottom-spacing-sys-spacing-2',
            'right-spacing-sys-spacing-2',
          ])}
          {...signal}
        />
      )}
      {additionIcon && (
        <div
          data-testid="avatar-addition"
          className={cn([
            'absolute',
            'bottom-spacing-sys-spacing-2',
            'right-spacing-sys-spacing-2',
            'rounded-radius-sys-radius-8',
            'border-color-ref-ui-background-base',
            'bg-color-ref-ui-background-accent',
            '[&>svg]:w-sizing-sys-size-16',
            '[&>svg]:h-sizing-sys-size-16',
            // `text-*` + `fill-current` instead of `fill-color-*` so sprite-
            // backed icons (whose `<path fill="currentColor">` overrides
            // inherited CSS fill) pick up the color via the inherited `color`.
            'text-color-ref-ui-icon-strong [&>svg]:fill-current',
          ])}
        >
          {additionIcon}
        </div>
      )}
    </div>
  );
};

Avatar.displayName = 'Avatar';

export {
  Avatar,
  avatarVariants,
  type AvatarProps,
  type AvatarVariant,
  type AvatarSize,
  type AvatarType,
  type AvatarBorder,
};
